import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { List, Spin, Empty, message, Card } from 'antd';
import breakdownService from '../services/breakdownService';
import { loadMechanicStatuses } from '../services/mechanicStatusService';
import BreakdownCard from './BreakdownCard';
import PullToRefresh from './PullToRefresh';
import { APP_CONFIG } from '../utils/constants';
import { useNavigate } from 'react-router-dom';
import '../assets/styles/breakdownList.css';
import { CompassOutlined } from '@ant-design/icons';

const BreakdownList = ({ userRole, searchTerm = '', dateRange, limit, companyFilter, onCompanyOptionsChange }) => {
  const navigate = useNavigate();
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mechanicStatuses, setMechanicStatuses] = useState([]);
  const term = typeof searchTerm === 'string' ? searchTerm.trim().toLowerCase() : '';

  const baseFilteredBreakdowns = useMemo(() => {
    let list = breakdowns || [];

    if (term) {
      list = list.filter((b) => {
        const ref = String(b.breakdownReference || '').toLowerCase();
        const reg = String(b.registrationNumber || '').toLowerCase();
        const fleet = String(b.fleetNumber || b.fleet || '').toLowerCase();
        return ref.includes(term) || reg.includes(term) || fleet.includes(term);
      });
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const toJsDate = (d) => {
        if (!d) return null;
        try {
          if (typeof d.toDate === 'function') return d.toDate();
        } catch { }
        try {
          if (typeof d.toISOString === 'function') return new Date(d.toISOString());
        } catch { }
        return new Date(d);
      };

      const fromDate = toJsDate(dateRange[0]);
      const toDate = toJsDate(dateRange[1]);

      if (fromDate && toDate) {
        list = list.filter((b) => {
          const rawDate =
            b.dateTimeOffset ||
            b.createdDate ||
            b.createdOn ||
            b.createdAt;
          if (!rawDate) return true;
          const bd = new Date(rawDate);
          if (Number.isNaN(bd.getTime())) return true;
          return bd >= fromDate && bd <= toDate;
        });
      }
    }

    return list;
  }, [breakdowns, term, dateRange]);

  const companyOptions = useMemo(() => {
    const namesSet = new Set();
    (baseFilteredBreakdowns || []).forEach((b) => {
      const name = (b.companyName || '').trim();
      if (name) {
        namesSet.add(name);
      }
    });
    return Array.from(namesSet)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }));
  }, [baseFilteredBreakdowns]);

  React.useEffect(() => {
    if (typeof onCompanyOptionsChange === 'function') {
      onCompanyOptionsChange(companyOptions);
    }
  }, [companyOptions, onCompanyOptionsChange]);

  const filteredBreakdowns = useMemo(() => {
    let list = baseFilteredBreakdowns || [];

    if (companyFilter) {
      list = list.filter(
        (b) => String(b.companyName || '').trim().toLowerCase() === companyFilter.toLowerCase()
      );
    }

    if (limit !== undefined && limit !== null && limit !== 'all') {
      const numericLimit = Number(limit);
      if (!Number.isNaN(numericLimit) && numericLimit > 0) {
        list = list.slice(0, numericLimit);
      }
    }

    return list;
  }, [baseFilteredBreakdowns, limit, companyFilter]);

  // Function to fetch breakdowns
  const fetchBreakdowns = useCallback(async () => {
    try {
      setRefreshing(true);

      const isMechanic = userRole === 'MECHANIC';
      // Call the correct API based on role
      const response = isMechanic
        ? await breakdownService.getAssignedBreakdowns()
        : await breakdownService.getVisibleBreakdowns({ statuses: [0, 1, 2, 3, 4, 5, 8, 9, 10, 18, 19, 20, 30, 40, 50, 60] });

      // Support multiple possible response shapes
      let apiBreakdowns = [];
      if (Array.isArray(response)) {
        apiBreakdowns = response;
      } else if (Array.isArray(response?.breakdowns)) {
        apiBreakdowns = response.breakdowns;
      } else if (Array.isArray(response?.data)) {
        apiBreakdowns = response.data;
      }

      if (response && response.success === false) {
        message.error('Failed to load breakdowns. Please try again later.');
      }

      setBreakdowns(apiBreakdowns);

      if (apiBreakdowns.length === 0) {
        message.info(isMechanic ? 'No breakdowns are currently assigned to you' : 'No active breakdowns to display');
      }
    } catch (error) {
      console.error('Error fetching breakdowns:', error);
      message.error('An error occurred while fetching breakdowns');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userRole]);

  // Handle manual refresh
  const handleRefresh = () => {
    setRefreshing(true);
    return new Promise((resolve) => {
      fetchBreakdowns().then(() => {
        // Resolve the promise after fetching breakdowns to complete the refresh
        resolve();
        setRefreshing(false);
      });
    });
  };

  // The handleRefresh function is now simpler, as the PullToRefresh component will handle the refresh logic

  // Initial data fetch and SSE setup
  useEffect(() => {
    fetchBreakdowns();
    loadMechanicStatuses().then(setMechanicStatuses);

    // If SSE disabled, use polling only
    if (!APP_CONFIG.USE_SSE) {
      console.log('SSE disabled via config; using polling');
      const intervalId = setInterval(() => {
        if (!document.hidden) {
          fetchBreakdowns();
        }
      }, APP_CONFIG.BREAKDOWN_REFRESH_INTERVAL);
      return () => clearInterval(intervalId);
    }

    // Import and setup SSE service
    const setupRealTimeUpdates = async () => {
      try {
        // Dynamically import the SSE service to prevent issues if not available
        const sseServiceModule = await import('../services/sseService');
        const sseService = sseServiceModule.default;

        console.log('Setting up real-time updates with SSE');

        // Connect to SSE service
        const connected = sseService.connect();

        if (!connected) {
          console.log('SSE connection failed, falling back to polling');
          // Fallback to polling if SSE fails
          const intervalId = setInterval(() => {
            if (!document.hidden) { // Only refresh if the page is visible
              fetchBreakdowns();
            }
          }, APP_CONFIG.BREAKDOWN_REFRESH_INTERVAL);

          return () => clearInterval(intervalId);
        }

        // Set up event listeners for different breakdown events
        const newBreakdownUnsubscribe = sseService.addEventListener('NEW_BREAKDOWN', () => {
          console.log('New breakdown received, refreshing list');
          fetchBreakdowns();
        });

        const updateBreakdownUnsubscribe = sseService.addEventListener('UPDATE_BREAKDOWN', () => {
          console.log('Breakdown update received, refreshing list');
          fetchBreakdowns();
        });

        const removeBreakdownUnsubscribe = sseService.addEventListener('REMOVE_BREAKDOWN', () => {
          console.log('Breakdown removal received, refreshing list');
          fetchBreakdowns();
        });

        // Clean up function to remove listeners and disconnect
        return () => {
          newBreakdownUnsubscribe();
          updateBreakdownUnsubscribe();
          removeBreakdownUnsubscribe();
          sseService.disconnect();
        };
      } catch (error) {
        console.error('Error setting up real-time updates:', error);

        // Fallback to polling if SSE setup fails
        console.log('Falling back to polling for updates');
        const intervalId = setInterval(() => {
          if (!document.hidden) { // Only refresh if the page is visible
            fetchBreakdowns();
          }
        }, APP_CONFIG.BREAKDOWN_REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
      }
    };

    const cleanupPromise = setupRealTimeUpdates();
    return () => {
      if (cleanupPromise && typeof cleanupPromise.then === 'function') {
        cleanupPromise.then((cleanup) => {
          if (typeof cleanup === 'function') cleanup();
        }).catch(() => { });
      }
    };
  }, [fetchBreakdowns]);

  const handleMapNavigation = () => {
    navigate('/map');
  }

  // Service worker registration intentionally disabled here; handled at app level

  return (
    <div className="breakdown-list-container">
      <PullToRefresh onRefresh={handleRefresh}>

        {loading ? (
          <div className="loading-container" style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '16px 0', marginTop: '16px' }}>
            <Spin size="large" />
          </div>
        ) : breakdowns.length === 0 ? (
          <Empty
            description={userRole === 'MECHANIC' ? 'No breakdowns currently assigned' : 'No active breakdowns to display'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            {refreshing && (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '16px 0', marginTop: '16px' }}>
                <Spin size="large" />
              </div>
            )}

            {(userRole === 'MECHANIC' || userRole === 'SUPPLIER' || userRole === 'OWNER') && <Card
              title="Quick Actions"
              variant="outlined"
              style={{
                marginTop: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              <div
                className="action-card"
                onClick={handleMapNavigation}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  borderRadius: '8px',
                  height: '100px',
                  display: 'flex',
                  flex: '1',
                  minWidth: '120px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <CompassOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Map</div>
              </div>
            </Card>}

            <List
              dataSource={filteredBreakdowns}
              renderItem={(breakdown) => (
                <List.Item className="breakdown-list-item">
                  <BreakdownCard breakdown={breakdown} mechanicStatuses={mechanicStatuses} />
                </List.Item>
              )}
            />
            {term && filteredBreakdowns.length === 0 && (
              <Empty description="No matching active breakdowns" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </>
        )}
      </PullToRefresh>
    </div>
  );
};

export default BreakdownList;
